import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { placeAccessPassIntoCart } from '../../../utilities/api'

import {
  formatCurrency,
  getSinglarOrPluralWord,
} from '../../../utilities/helpers'

import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'

import { BackButton } from '../../BackButton'
import { Spinner } from '../../LoadingContainer/Spinner'

const MAX_SELECTABLE_PASSES = 20

export default function AccessPassDetails({ accessPassTemplate }) {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [error, setError] = useState('')

  const isSoldOut = Boolean(accessPassTemplate?.isSoldOut)

  const quantityOptions = useMemo(() => {
    if (isSoldOut) return []
    const remainingRaw = accessPassTemplate?.qtyRemaining
    const remaining =
      remainingRaw === null || remainingRaw === undefined || remainingRaw === ''
        ? null
        : Number(remainingRaw)
    // No inventory cap configured → allow the full selectable range.
    // Number(null) is 0, so treat null/undefined as unlimited.
    const max =
      remaining == null || !Number.isFinite(remaining)
        ? MAX_SELECTABLE_PASSES
        : Math.max(1, Math.min(MAX_SELECTABLE_PASSES, remaining))

    return Array.from({ length: max }, (_, index) => index + 1)
  }, [isSoldOut, accessPassTemplate?.qtyRemaining])

  const selectedQuantity = quantityOptions.includes(quantity)
    ? quantity
    : quantityOptions[0] || 1

  const handleGoBack = () => {
    navigate(`/venue/${accessPassTemplate?.venue?.slug}`)
  }

  const checkout = () => {
    setIsLoading(true)
    setError('')
    placeAccessPassIntoCart(accessPassTemplate.id, selectedQuantity)
      .then((res) => {
        sessionStorage.setItem('cart', JSON.stringify({ cartId: res.data.id }))
        navigate('/checkout')
      })
      .catch((err) => {
        console.error(err)
        setError(
          err?.response?.data?.error?.message ||
            'Unable to add access pass to cart.'
        )
      })
      .finally(() => setIsLoading(false))
  }

  const eventCount = accessPassTemplate?.events?.length || 0
  const total = (Number(accessPassTemplate?.price) || 0) * selectedQuantity

  return (
    <div id="view-access-pass">
      <Card className="about-card card-xl card-xl--dark full-height">
        <Card.Body className="d-flex-column align-items-start gap-4">
          <BackButton handleGoBack={handleGoBack} marginBottom="0" />
          <div className="d-flex-column gap-4 flex-grow-1 w-100">
            <div className="card-img-container">
              <Card.Img
                src={accessPassTemplate?.artwork?.url}
                width="114"
                height="114"
                alt={`Cover art for ${accessPassTemplate?.name} access pass`}
              />
            </div>
            <div className="d-flex-column gap-3">
              <Card.Subtitle as="h6">
                {accessPassTemplate?.organization?.name}
              </Card.Subtitle>
              <Card.Title as="h5" className="m-0">
                {accessPassTemplate?.name}
              </Card.Title>
              <Card.Text className="location text-secondary">
                {accessPassTemplate?.venue?.name}
              </Card.Text>
              {eventCount > 0 && (
                <Card.Text className="small text-muted mb-0">
                  Includes access to {eventCount}{' '}
                  {eventCount === 1 ? 'event' : 'events'}
                </Card.Text>
              )}
            </div>
            <div className="seat-quantity align-items-center gap-4 justify-content-between">
              <Stack>
                <Card.Title as="h5" className="card-title-sm">
                  Pass quantity
                </Card.Title>
                <p className="small mb-0">
                  Select how many passes you want to buy.
                </p>
              </Stack>
              <Form.Select
                aria-label="Pass quantity"
                value={isSoldOut ? '' : selectedQuantity}
                disabled={isSoldOut || isLoading}
                onChange={(e) => setQuantity(Number(e.target.value))}
              >
                {isSoldOut ? (
                  <option value="">Sold out</option>
                ) : (
                  quantityOptions.map((option) => (
                    <option value={option} key={option}>
                      {option} {getSinglarOrPluralWord(option, 'Pass')}
                    </option>
                  ))
                )}
              </Form.Select>
            </div>
            {error && (
              <p className="small text-danger mb-0" role="alert">
                {error}
              </p>
            )}
            <div className="gap-2 mt-auto d-flex-column flex-md-row align-items-md-center mt-auto justify-content-md-between">
              <div className="d-flex-column gap-1 flex-grow-1">
                <span className="normal">
                  Total{' '}
                  <span className="fw-semi-bold">{formatCurrency(total)}</span>
                </span>
              </div>
              <Button
                size="lg"
                id="checkout-btn"
                className={`icon-button mt-0 ${!isLoading ? 'btn-next' : ''} flex-grow-1`}
                disabled={isLoading || isSoldOut}
                onClick={checkout}
              >
                {isLoading ? <Spinner /> : isSoldOut ? 'Sold out' : 'Checkout'}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}
